import {
	VisitTypeOptions,
	VisitTypeEnum,
	FrequencyTypeOptions,
	type FrequencyViewModel,
	type SearchFrequencyViewModel,
	FrequencyTypeEnum,
	FrequencyStatusEnum,
	FrequencyCadenceTypeEnum,
	getFrequenciesInEffectForOrderOptions
} from '@generated';
import type { CalendarEventModel } from '~/shared/calendar/types';
import { addHours, formatToShortDateAndTime, getMonth, getWeek, startOfDay } from '~/util/date';
import { getDayOfYear, getYear, type Day } from 'date-fns';
import { getDisciplineIntent } from '~/libraries/disciplines/util';
import { AlertTriangle, Ban, Check, User, X } from 'lucide-react';
import { getSearchQueryKey } from '~/util/http/useSearch';
import type { StatusAppearances } from '~/theme/variants';
import type { DraftEvaluationFrequencyModel, DraftRoutineFrequencyModel } from './types';

export function getInvalidateFrequencyQueryKeys(orderId: string) {
	return [getSearchQueryKey('frequency'), getFrequenciesInEffectForOrderOptions(orderId).queryKey];
}

export function getCadenceCorrelator(frequencyCadenceTypeId: number, scheduledDateAndTime: Date, agencyStartOfWeek: number): number {
	switch (frequencyCadenceTypeId) {
		case FrequencyCadenceTypeEnum.Daily:
		case FrequencyCadenceTypeEnum.Evaluation:
			return getDayOfYear(scheduledDateAndTime);
		case FrequencyCadenceTypeEnum.Weekly:
			return getWeek(scheduledDateAndTime, { weekStartsOn: agencyStartOfWeek as Day });
		case FrequencyCadenceTypeEnum.Monthly:
			return getYear(scheduledDateAndTime) * 12 + getMonth(scheduledDateAndTime);
		default:
			return 0;
	}
}

export function isSameCadenceCorrelator(frequencyCadenceTypeId: number, existing: Date, proposed: Date, agencyStartOfWeek: number): boolean {
	if (frequencyCadenceTypeId === FrequencyCadenceTypeEnum.Evaluation) {
		return true;
	} else if (frequencyCadenceTypeId === FrequencyCadenceTypeEnum.SpecificDateAndTime) {
		return existing.getTime() === proposed.getTime();
	}
	return (
		getCadenceCorrelator(frequencyCadenceTypeId, existing, agencyStartOfWeek) ===
		getCadenceCorrelator(frequencyCadenceTypeId, proposed, agencyStartOfWeek)
	);
}

function getFrequencyAppearance(frequency: FrequencyViewModel | SearchFrequencyViewModel): StatusAppearances {
	if (frequency.frequencyStatusId === FrequencyStatusEnum.Missed || frequency.frequencyStatusId === FrequencyStatusEnum.DiscontinueDrafted) {
		return 'solidMuted';
	} else if (frequency.invariants.canBeDiscontinued) {
		return 'solid';
	} else if (frequency.frequencyStatusId === FrequencyStatusEnum.PendingMissedVisit) {
		return 'outlineDashed';
	}
	return 'outline';
}

function getFrequencyCalendarIcon(frequency: FrequencyViewModel | SearchFrequencyViewModel) {
	if (frequency.frequencyStatusId === FrequencyStatusEnum.Performed || frequency.frequencyStatusId === FrequencyStatusEnum.Performing) {
		return [Check];
	} else if (frequency.frequencyStatusId === FrequencyStatusEnum.PendingMissedVisit) {
		return [AlertTriangle];
	} else if (frequency.frequencyStatusId === FrequencyStatusEnum.DiscontinueDrafted) {
		return [X];
	} else if (frequency.frequencyStatusId === FrequencyStatusEnum.Missed) {
		return [Ban];
	} else if (frequency.assignedToId) {
		return [User];
	}
	return [];
}

export function convertFrequenciesToCalendarEvent(frequencies: (FrequencyViewModel | SearchFrequencyViewModel)[]): CalendarEventModel[] {
	return frequencies.map(frequency => {
		const frequencyType = FrequencyTypeOptions.find(x => x.id === frequency.frequencyTypeId)!;
		return {
			id: frequency.id,
			title:
				frequency.frequencyTypeId === FrequencyTypeEnum.Evaluation
					? 'Eval'
					: frequency.frequencyTypeId === FrequencyTypeEnum.Routine
					? 'Rout'
					: frequencyType.label,
			isReadonly: !frequency.invariants.canBeEdited,
			canBeRescheduled: frequency.invariants.canBeRescheduled,
			startTime: frequency.scheduledDateAndTime,
			endTime: addHours(frequency.scheduledDateAndTime, 1),
			startAndEndTimeFormatted: formatToShortDateAndTime(frequency.scheduledDateAndTime),
			intent:
				frequency.frequencyStatusId === FrequencyStatusEnum.DiscontinueDrafted ? 'destructive' : getDisciplineIntent(frequency.disciplineId),
			appearance: getFrequencyAppearance(frequency),
			Icons: getFrequencyCalendarIcon(frequency)
		};
	});
}

export function convertDraftFrequenciesToCalendarEvent(
	frequencies: {
		id: string;
		frequencyTypeId: number;
		scheduledDateAndTime: Date;
		disciplineId: number;
		assignedToId?: string;
		isReadOnly: boolean;
	}[]
): CalendarEventModel[] {
	return frequencies.map(frequency => {
		const frequencyType = FrequencyTypeOptions.find(x => x.id === frequency.frequencyTypeId)!;
		return {
			id: frequency.id,
			title:
				frequency.frequencyTypeId === FrequencyTypeEnum.Evaluation
					? 'Eval'
					: frequency.frequencyTypeId === FrequencyTypeEnum.Routine
					? 'Rout'
					: frequencyType.label,
			isReadonly: false,
			canBeRescheduled: true,
			startTime: frequency.scheduledDateAndTime,
			endTime: addHours(frequency.scheduledDateAndTime, 1),
			startAndEndTimeFormatted: formatToShortDateAndTime(frequency.scheduledDateAndTime),
			intent: getDisciplineIntent(frequency.disciplineId),
			appearance: !frequency.isReadOnly ? 'solid' : 'outline',
			Icons: frequency.assignedToId ? [User] : []
		};
	});
}

export function createNewRoutineFrequency(
	startDate: Date,
	frequencyCadenceTypeId: number,
	disciplineId: number,
	assignedToId?: string
): DraftRoutineFrequencyModel {
	const frequencyStartTime = addHours(startOfDay(startDate), 8);
	const inPersionVisitType = VisitTypeOptions.find(x => x.id === VisitTypeEnum.InPerson)!;
	return {
		visitTypeId: inPersionVisitType.id,
		disciplineId,
		frequencyCadenceTypeId,
		scheduledDateAndTime: frequencyStartTime,
		id: window.crypto.randomUUID(),
		assignedToId: assignedToId
	};
}

export function createNewEvaluationFrequency(startDate: Date, disciplineId: number, assignedToId?: string): DraftEvaluationFrequencyModel {
	const frequencyStartTime = addHours(startOfDay(startDate), 8);
	return {
		disciplineId,
		scheduledDateAndTime: frequencyStartTime,
		id: window.crypto.randomUUID(),
		assignedToId: assignedToId,
		physicianRequested: false
	};
}
